import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import shortId from 'shortid'

import { tagWrapperItemsPropType } from './prop-types/tag-wrapper-items-prop-type'
import TagWrapperMeasurementHelper from './tag-wrapper-measurement-helper'
import { PlusTag } from './tag-wrapper-plus-tag'
import { TagWrapperTooltip } from './tag-wrapper-tooltip'

import styles from './tag-wrapper.module.css'

const propTypes = {
    className: PropTypes.string,
    handleResize: PropTypes.bool,
    items: tagWrapperItemsPropType.isRequired,
    maxTooltipItems: PropTypes.number,
    renderItem: PropTypes.func,
    tagClassName: PropTypes.string,
    tooltipClassName: PropTypes.string,
    tagPadding: PropTypes.number,
    tooltipPlacement: PropTypes.string
}

const defaultProps = {
    handleResize: false,
    maxTooltipItems: 10,
    renderItem: item => item,
    tagPadding: 22,
    tooltipPlacement: 'left'
}

const HANDLE_RESIZE_DEBOUNCE_TIME = 100

/**
 * A wrapper that fits as many tags as possible into its available space from a given
 * list of items before showing an extra '+' tag with a tooltip containing the remaining items.
 *
 * @property {Object[]} props.items                - A list of items to be rendered as Tags.
 * @property {Function} props.renderItem           - A function to render the items.
 * @property {number} props.maxTooltipItems        - The maximum number of items to be shown in the tooltip.
 * @property {string} props.tooltipPlacement       - Placement string for the tooltip.
 * @property {string} props.className              - Custom className for the component.
 * @property {boolean} props.handleResize          - Should the component listen to window resize events
 * @property {string} props.tagClassName           - Custom className for tags.
 * @property {string} props.tooltipClassName       - Custom className for the tooltip.
 */
class TagWrapper extends Component {
    constructor(props) {
        super(props)

        this.getMaxWidth = this.getMaxWidth.bind(this)
        this.handleResize = this.handleResize.bind(this)
        this.renderPlusTag = this.renderPlusTag.bind(this)
        this.renderTag = this.renderTag.bind(this)
        this.renderTags = this.renderTags.bind(this)
        this.setVisibleTagQuantity = this.setVisibleTagQuantity.bind(this)
        this.storeContainerWidth = this.storeContainerWidth.bind(this)
        this.storeRef = this.storeRef.bind(this)
        this.resizeTimeout = null

        this.state = {
            containerWidth: null,
            maxWidth: null,
            visibleTagQuantity: 0
        }
    }

    componentDidMount() {
        this.storeContainerWidth()

        if (this.props.handleResize) {
            window.addEventListener('resize', this.handleResize)
        }
    }

    componentDidUpdate() {
        this.storeContainerWidth()
    }

    componentWillUnmount() {
        if (this.props.handleResize) {
            window.removeEventListener('resize', this.handleResize)
        }

        clearTimeout(this.resizeTimeout)
    }

    render() {
        const { className, items, renderItem, tagClassName } = this.props
        const { maxWidth } = this.state
        const composedClassName = cx(styles['tag-wrapper-container'], className)

        if (!Array.isArray(items) || !items.length) {
            return null
        }

        return (
            <div ref={this.storeRef} className={composedClassName}>
                <span className={styles['tag-wrapper-container-inner']}>
                    {this.renderTags()}
                    {maxWidth && (
                        <TagWrapperMeasurementHelper
                            invisibleIndicator={this.renderPlusTag()}
                            setVisibleTagQuantity={this.setVisibleTagQuantity}
                            renderTag={this.renderTag}
                            {...{ items, maxWidth, renderItem, tagClassName }}
                        />
                    )}
                </span>
            </div>
        )
    }

    setVisibleTagQuantity(newVisibleQuantity) {
        const { visibleTagQuantity } = this.state

        visibleTagQuantity !== newVisibleQuantity &&
            this.setState({
                visibleTagQuantity: newVisibleQuantity
            })
    }

    /**
     * This function renders the plus tag with the number of invisible tabs.
     *
     * @returns {ReactElement}
     */
    renderPlusTag() {
        const { items, tagClassName } = this.props
        const { visibleTagQuantity } = this.state

        const invisibleTags = visibleTagQuantity > 0 ? items.length - visibleTagQuantity : 0

        return <PlusTag key="tag-wrapper-invisible-indicator" numItems={invisibleTags} {...{ tagClassName }} />
    }

    /**
     * This function render the given item with the renderItem function and extend it with className and a key.
     *
     * @param {ReactElement} item - The item which should get rendered
     * @returns {ReactElement}
     */
    renderTag(item) {
        const { renderItem, tagClassName } = this.props

        const renderedItem = item && renderItem(item)

        if (!renderedItem) {
            return null
        }

        return React.cloneElement(renderedItem, {
            className: cx(styles['tag'], tagClassName, 't3sel-tag-wrapper-tag'),
            key: `tag-wrapper-item-${shortId.generate()}`
        })
    }

    /**
     * Renders the tags which will be rendered into the tag wrapper.
     *
     * @return {React.element|null}
     */
    renderTags() {
        const { items, maxTooltipItems, tagClassName, tooltipClassName, tooltipPlacement } = this.props
        const { visibleTagQuantity } = this.state

        const visibleItems = items.slice(0, visibleTagQuantity)
        const invisibleItems = items.slice(visibleTagQuantity)
        const visibleTags = visibleItems.map(item => this.renderTag(item))
        const invisibleTags = invisibleItems.map(
            (item, index) => (index < maxTooltipItems && this.renderTag(item)) || null
        )

        return invisibleItems.length
            ? visibleTags.concat(
                  <TagWrapperTooltip
                      items={invisibleTags}
                      key="tag-wrapper-tooltip"
                      {...{ maxTooltipItems, tagClassName, tooltipClassName, tooltipPlacement }}
                  />
              )
            : visibleTags
    }

    /**
     * Get the max with of the rendered container for measurement.
     *
     * @returns {number}
     */
    getMaxWidth() {
        return this.ref ? this.ref.offsetWidth - this.props.tagPadding : 0
    }

    storeRef(ref) {
        this.ref = ref
    }

    storeContainerWidth() {
        const { containerWidth } = this.state
        const newWidth = this.ref ? this.ref.offsetWidth : 0

        // for half-pixel offsetWidth does not reliably round up or down,
        // so for a bad combination of tagname + number this can cause an endless render loop.
        // to prevent this, we cannot rerender for small size diffrences
        if (
            (typeof containerWidth !== 'number' && typeof newWidth === 'number') ||
            Math.abs(containerWidth - newWidth) >= 2
        ) {
            this.setState({
                containerWidth: newWidth,
                maxWidth: this.getMaxWidth()
            })
        }
    }

    handleResize() {
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = setTimeout(this.storeContainerWidth, HANDLE_RESIZE_DEBOUNCE_TIME)
    }
}

TagWrapper.propTypes = propTypes
TagWrapper.defaultProps = defaultProps

export default TagWrapper
